import type { Tour } from './types'

/** The first-login tour: main navigation and key concepts. */
export const WELCOME_TOUR_ID = 'welcome'

export const TOURS: Tour[] = [
  {
    id: WELCOME_TOUR_ID,
    title: 'Знайомство з Dovira AI Bridge',
    steps: [
      {
        title: 'Вітаємо в Dovira AI Bridge',
        body: 'Bridge збирає в одному місці дані Enote, телефонії AI SIP, камер у кабінетах, касових апаратів і бухгалтерії. За хвилину покажемо головне. Тур можна призупинити (Esc) і продовжити з довідки.',
      },
      {
        anchor: ['nav', 'nav-mobile'],
        title: 'Меню розділів',
        body: 'Тут усі розділи, доступні вашій ролі. Швидкий перехід з клавіатури: G, а потім літера розділу (наприклад, G T — Завдання).',
      },
      {
        anchor: 'nav-today',
        title: '«Сьогодні»',
        body: 'Стартова панель дня: записи, запізнення, дзвінки без запису, аналізи, ваші завдання й прострочені чеклісти. Кожен віджет веде туди, де питання вирішується.',
      },
      {
        anchor: 'nav-journeys',
        title: 'Візити й подорожі',
        body: 'Кожен візит — це «подорож»: дзвінок → запис → кабінет → лікування → оплата → рекомендації → оцінка. Позначки підкажуть, чого бракує (немає чека, рекомендацій, зв’язку з дзвінком).',
      },
      {
        anchor: 'nav-registry',
        title: 'Єдиний реєстр',
        body: 'Кожна людина, організація й тварина — один запис, до якого прив’язані картки з Enote, BAS, Checkbox, AI SIP і кадрів. Так клієнт, ФОП і оператор з різних систем стають однією сутністю.',
      },
      {
        anchor: 'nav-tasks',
        title: 'Завдання',
        body: 'Доручення команди з термінами й нагадуваннями. Нове завдання можна створити з будь-якої сторінки клавішею C.',
      },
      {
        anchor: 'search',
        title: 'Пошук і команди',
        body: 'Ctrl+K (або «/») — знайти клієнта, тварину, дзвінок чи перейти в розділ, не торкаючись миші.',
      },
      {
        anchor: 'notifications',
        title: 'Сповіщення',
        body: 'Нові завдання, коментарі, коди, прострочення. Ті самі сповіщення можна отримувати в Telegram — налаштуйте в профілі.',
      },
      {
        anchor: 'help',
        title: 'Довідка на кожній сторінці',
        body: 'Кнопка «?» (або клавіші ? чи F1) відкриває довідку саме для поточної сторінки: що тут робити, що означають кнопки й статуси, звідки дані. Там же — тури по сторінках.',
      },
      {
        anchor: 'user-menu',
        title: 'Профіль і налаштування',
        body: 'Мова, тема (світла / темна), гарячі клавіші, профіль і повтор цього туру.',
      },
      {
        title: 'Готово!',
        body: 'На великих сторінках (Візити, ВРІТ, Постачальники, Гроші, Кадри, Реєстр, Завдання) є власні короткі тури — шукайте кнопку «Пройти тур цією сторінкою» в довідці.',
      },
    ],
  },
  {
    id: 'page.journeys',
    title: 'Візити',
    route: '/journeys',
    steps: [
      { anchor: 'page-header', title: 'Візити як подорожі', body: 'Кожен рядок — один візит від дзвінка до оцінки. Відкрийте рядок, щоб побачити хронологію.' },
      { anchor: 'journeys-stats', title: 'Підсумки періоду', body: 'Скільки візитів, яка частка записів з дзвінком і візитів з транскриптом, скільки втрачених звернень.' },
      { anchor: 'journeys-filters', title: 'Фільтри', body: 'Період, пошук за кличкою чи клієнтом, стан і позначки. Наприклад, «Позначки → Немає чека» покаже всі візити без фіскального чека.' },
      { anchor: 'journeys-missing-links', title: 'Без зв’язків', body: 'Візити, де дзвінок чи запис з кабінету ще не прив’язані впевнено.' },
      { anchor: 'journeys-review', title: 'Перевірка зв’язків', body: 'Черга пропозицій «це той самий візит?». Клавіші Y / N підтверджують чи відхиляють, J / K — наступна / попередня.' },
      { anchor: 'journeys-table', title: 'Таблиця візитів', body: 'Етапи показано смугою: зелений — виконано, жовтий — потребує уваги. Клацніть рядок, щоб відкрити візит.' },
    ],
  },
  {
    id: 'page.vrit',
    title: 'ВРІТ',
    route: '/vrit',
    steps: [
      { anchor: 'page-header', title: 'Дошка стаціонару', body: 'Бокси, пацієнти, категорії та відлік до наступного контролю. Оновлюється щохвилини.' },
      { anchor: 'vrit-branch', title: 'Філія', body: 'Перемикайте філію або дивіться обидві.' },
      { anchor: 'vrit-stats', title: 'Що потребує уваги', body: 'Нерозміщені пацієнти, прострочені контролі, звіти власникам і передачі, що чекають підтвердження.' },
      { anchor: 'vrit-admit', title: 'Прийняти пацієнта', body: 'Оберіть пацієнта з Enote, бокс, категорію та вагу. Без підписаного пакета згод прийняти може лише лікар із причиною.' },
      { anchor: 'vrit-sync', title: 'Синхронізація з Enote', body: 'Пацієнти, яких Enote показує у стаціонарі, з’являються на дошці автоматично; кнопка звіряє негайно.' },
      { anchor: 'vrit-unplaced', title: 'Нерозміщені', body: 'Пацієнти з Enote без боксу. Відкрийте картку, розмістіть у бокс і вкажіть категорію.' },
      { anchor: 'vrit-board', title: 'Бокси', body: 'Картка пацієнта: категорія, день перебування, відлік до контролю. Відкрийте її для моніторингу, передач, звіту власнику й виписки.' },
      { anchor: 'vrit-handover', title: 'Ранкова передача', body: 'Передача зміни по всіх пацієнтах з підтвердженням лікарем ВРІТ.' },
    ],
  },
  {
    id: 'page.supply',
    title: 'Постачальники й рахунки',
    route: '/supply',
    steps: [
      { anchor: 'page-header', title: 'Накладні з таймером', body: 'Кожна накладна має 24 години на оплату. Прострочені — вгорі й у сповіщеннях.' },
      { anchor: 'supply-register', title: 'Зареєструвати накладну', body: 'Сфотографуйте накладну й оберіть філію — номер присвоїться автоматично. Можна й через Telegram-бота командою /invoice.' },
      { anchor: 'supply-tabs', title: 'Вкладки', body: 'Накладні, картки постачальників і акти звірки.' },
      { anchor: 'supply-filters', title: 'Фільтри', body: 'Пошук, філія, лише прострочені, а також «Увага: безнал не внесено» — безготівкові накладні, яких немає в Enote.' },
      { anchor: 'supply-totals', title: 'Підсумки', body: 'Не оплачено, прострочено, з розбіжностями, не оприбутковано. Суми бачить лише фінансист.' },
    ],
  },
  {
    id: 'page.money',
    title: 'Гроші',
    route: '/finance/money',
    steps: [
      { anchor: 'page-header', title: 'Одна оплата — одна подія', body: 'Оплата Enote, фіскальний чек (Enote чи Checkbox), документ BAS і банківська виписка стоять поруч в одній події.' },
      { anchor: 'money-tabs', title: 'Реєстр, черги, денні документи', body: 'Почніть день з «Черги» — там усе, що потребує рішення людини.' },
      { anchor: 'money-filters', title: 'Фільтри', body: 'ФОП, вид, стан, спосіб оплати, повнота, джерела й пошук за номером чека чи клієнтом.' },
      { anchor: 'money-rebuild', title: 'Перезібрати', body: 'Заново зібрати події за період, якщо дані в джерелах змінилися.' },
      { anchor: 'money-table', title: 'Події', body: 'Стан «Повна» — усе збігається. «Неповна», «Без оплати», «Розбіжність» — відкрийте подію й порівняйте відображення: відмінне поле виділено червоним.' },
    ],
  },
  {
    id: 'page.personnel',
    title: 'Кадри',
    route: '/personnel',
    steps: [
      { anchor: 'page-header', title: 'Кадровий облік', body: 'Особові справи П-2, штатний розклад, накази, відпустки й лікарняні — за законом.' },
      { anchor: 'personnel-employer', title: 'Роботодавець', body: 'Оберіть ФОП чи ТОВ, за яким ведеться облік, або дивіться всіх.' },
      { anchor: 'personnel-tabs', title: 'Розділи', body: '«Перевірка й нагадування» покаже, чого бракує за законом і що спливає; «Зв’язок з BAS» — збіги з працівниками бухгалтерії.' },
      { anchor: 'personnel-content', title: 'Працівники', body: 'Відкрийте працівника: особова справа з вкладками, «Картка П-2 (PDF)». Персональні дані розкриваються окремою кнопкою і записуються в аудит.' },
    ],
  },
  {
    id: 'page.registry',
    title: 'Єдиний реєстр',
    route: '/registry',
    steps: [
      { anchor: 'page-header', title: 'Один запис на кожного', body: 'Людина, організація чи тварина — одна сутність, до якої прив’язані картки з усіх систем.' },
      { anchor: 'registry-tabs', title: 'Вкладки', body: '«Пошук», «Перевірка збігів» (рішення «те саме / різні») і «Покриття» джерел.' },
      { anchor: 'registry-search', title: 'Пошук', body: 'Прізвище будь-яким написанням, телефон, e-mail, ЄДРПОУ або id. «Лише з кількох джерел» — сутності, знайдені в двох і більше системах.' },
      { anchor: 'registry-kind', title: 'Тип', body: 'Звузьте до людей, організацій або тварин.' },
      { anchor: 'registry-results', title: 'Результати', body: 'Відкрийте сутність: відомості, пов’язані записи й зв’язки. Помилку виправляють «Об’єднати з…» або «Відокремити».' },
    ],
  },
  {
    id: 'page.tasks',
    title: 'Завдання',
    route: '/tasks',
    steps: [
      { anchor: 'tasks-new', title: 'Нове завдання', body: 'Назва, виконавець, термін і пріоритет. З будь-якої сторінки — клавіша C.' },
      { anchor: 'tasks-series', title: 'Повторювані', body: 'Серія створює завдання за розкладом — наприклад, щопонеділка перевірити залишки вакцин.' },
      { anchor: 'tasks-views', title: 'Вигляд', body: 'Список, дошка (перетягуйте картки між статусами), календар термінів і перелік серій.' },
      { anchor: 'tasks-mine', title: 'Мої й прострочені', body: 'Швидкі фільтри для щоденної роботи.' },
    ],
  },
]

export function tourById(id: string | undefined): Tour | undefined {
  return id ? TOURS.find((t) => t.id === id) : undefined
}

/** Every data-tour id the tours reference (for the source-coverage test). */
export function tourAnchors(): string[] {
  const ids = new Set<string>()
  for (const t of TOURS) for (const s of t.steps) for (const a of [s.anchor ?? []].flat()) ids.add(a)
  return [...ids]
}
